import React, { useEffect, useMemo, useState } from "react"

import { format } from "date-fns"
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Circle, Search } from "lucide-react"

import { ComplianceFiltersPopover } from "@/components/compliance/filters/ComplianceFiltersPopover"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useComplianceFilters } from "@/contexts/ComplianceFiltersContext"
import { usePeopleDataCompleteness } from "@/hooks/usePeopleDataCompleteness"

// Helper function to check if a field is missing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFieldMissing = (value: any): boolean => {
  if (value === null || value === undefined) return true
  if (typeof value === "string" && value.trim() === "") return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

// Component to render cell with missing data indicator
const DataCell: React.FC<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatValue?: (val: any) => string
}> = ({ value, formatValue }) => {
  const isMissing = isFieldMissing(value)
  const displayValue = isMissing
    ? "Missing"
    : formatValue
      ? formatValue(value)
      : Array.isArray(value)
        ? value.join(", ")
        : String(value)

  if (isMissing) {
    return (
      <div className="flex items-center gap-2 min-w-0 border border-red-500 rounded px-2 py-1 bg-red-50 flex-shrink-0">
        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
        <span className="text-red-600 font-medium whitespace-nowrap">{displayValue}</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="whitespace-nowrap overflow-x-auto">{displayValue}</span>
    </div>
  )
}

const ComplianceDataCompletenessTab: React.FC = () => {
  const {
    searchQuery,
    setSearchQuery,
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
  } = useComplianceFilters()
  const [statusFilter, setStatusFilter] = useState("Active")
  const [dataCompletenessFilter, setDataCompletenessFilter] = useState<
    "all" | "missing" | "complete"
  >("all")
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  useEffect(() => {
    setCurrentPage(0)
  }, [
    searchQuery,
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
    statusFilter,
  ])

  // Fetch all records for completeness filtering
  // We'll paginate client-side after filtering
  const { data, isLoading, error } = usePeopleDataCompleteness({
    searchQuery,
    statusFilter: statusFilter !== "all" ? statusFilter : "",
    locationFilter: locationFilter !== "all" ? locationFilter : "",
    sheriffTypeFilter: sheriffTypeFilter !== "all" ? sheriffTypeFilter : "",
    driverManagerTeamFilter: driverManagerTeamFilter !== "all" ? driverManagerTeamFilter : "",
    dspFilter: dspFilter !== "all" ? dspFilter : "",
    // No limit = fetch all records
  })

  const allRecords = data?.records ?? []

  // Helper function to check if a record has all required fields
  const hasAllRequiredFields = (record: (typeof allRecords)[0]): boolean => {
    const requiredFields = [
      record.person_id,
      record.first_name,
      record.surname,
      record.date_of_birth,
      record.email,
      record.address_line_1,
      record.city,
      record.postcode,
      record.phone_number,
      record.bank_name,
      record.name_on_bank_account,
      record.bank_account_number,
      record.bank_sort_code,
      record.contract_start_date,
      record.pay_basis,
      record.pay_group,
      record.sheriff_areas,
    ]

    return requiredFields.every((field) => {
      if (field === null || field === undefined) return false
      if (typeof field === "string" && field.trim() === "") return false
      if (Array.isArray(field) && field.length === 0) return false
      return true
    })
  }

  // Filter records based on data completeness
  const filteredRecords = useMemo(() => {
    if (dataCompletenessFilter === "all") {
      return allRecords
    } else if (dataCompletenessFilter === "complete") {
      return allRecords.filter(hasAllRequiredFields)
    } else {
      // missing
      return allRecords.filter((record) => !hasAllRequiredFields(record))
    }
  }, [allRecords, dataCompletenessFilter])

  // Apply pagination to filtered records
  const paginatedRecords = useMemo(() => {
    const start = currentPage * pageSize
    const end = start + pageSize
    return filteredRecords.slice(start, end)
  }, [filteredRecords, currentPage, pageSize])

  const total = filteredRecords.length
  const totalPages = Math.ceil(total / pageSize)
  const startRecord = currentPage * pageSize + 1
  const endRecord = Math.min((currentPage + 1) * pageSize, total)

  // Count records by completeness for filter buttons
  const completenessCounts = useMemo(() => {
    const complete = allRecords.filter(hasAllRequiredFields).length
    const missing = allRecords.filter((record) => !hasAllRequiredFields(record)).length
    return {
      all: allRecords.length,
      complete,
      missing,
    }
  }, [allRecords])

  const handleFilterChange = () => {
    setCurrentPage(0) // Reset to first page when filter changes
  }

  const handleDataCompletenessFilter = (filter: "all" | "missing" | "complete") => {
    setDataCompletenessFilter(filter)
    setCurrentPage(0) // Reset to first page when filter changes
  }

  const completenessFilterOptions = [
    {
      value: "all" as const,
      label: "All",
      count: completenessCounts.all,
      Icon: Circle,
      activeClasses: "bg-blue-50 text-blue-700 border-blue-200",
      badgeClasses: "bg-blue-100 text-blue-700",
      iconColor: "text-blue-500",
      iconActive: "text-blue-600",
    },
    {
      value: "complete" as const,
      label: "Complete",
      count: completenessCounts.complete,
      Icon: CheckCircle2,
      activeClasses: "bg-green-50 text-green-700 border-green-200",
      badgeClasses: "bg-green-100 text-green-700",
      iconColor: "text-green-500",
      iconActive: "text-green-600",
    },
    {
      value: "missing" as const,
      label: "Missing Data",
      count: completenessCounts.missing,
      Icon: AlertCircle,
      activeClasses: "bg-red-50 text-red-700 border-red-200",
      badgeClasses: "bg-red-100 text-red-700",
      iconColor: "text-red-500",
      iconActive: "text-red-600",
    },
  ]

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-palette-jungleGreen900">Data Completeness</h1>
        <p className="text-sm text-palette-jungleGreen600 mt-1">
          View and track required data fields for all people. Missing data is flagged with an
          indicator badge.
        </p>
      </div>

      {/* Data Completeness Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {completenessFilterOptions.map((option) => {
          const isActive = dataCompletenessFilter === option.value
          return (
            <button
              key={option.value}
              onClick={() => handleDataCompletenessFilter(option.value)}
              className={`inline-flex items-center gap-3 rounded-sm border px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? option.activeClasses
                  : "bg-white text-palette-jungleGreen700 border-palette-jungleGreen200 hover:bg-palette-jungleGreen000"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <option.Icon
                  className={`h-4 w-4 ${isActive ? option.iconActive : option.iconColor}`}
                />
                {option.label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  isActive
                    ? option.badgeClasses
                    : "bg-palette-jungleGreen100 text-palette-jungleGreen600"
                }`}
              >
                {option.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-palette-jungleGreen500" />
          <Input
            placeholder="Search by name, email, or person ID"
            className="pl-8"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setCurrentPage(0)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="status-filter"
            className="text-xs font-medium text-palette-jungleGreen700 sr-only"
          >
            Status
          </label>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value)
              handleFilterChange()
            }}
          >
            <SelectTrigger id="status-filter" className="w-[140px] h-10">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ComplianceFiltersPopover
          title="Filter People"
          description="Refine your search with filters below"
          showEligibilityFilter={false}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* Pagination Info */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-palette-jungleGreen600">
          {total > 0 ? `${startRecord} - ${endRecord} of ${total}` : "0 results"}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value))
              setCurrentPage(0)
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md flex-1 overflow-auto bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[100px]">Person ID</TableHead>
                <TableHead className="min-w-[120px]">First Name</TableHead>
                <TableHead className="min-w-[120px]">Last Name</TableHead>
                <TableHead className="min-w-[100px]">Status</TableHead>
                <TableHead className="min-w-[110px]">Date of Birth</TableHead>
                <TableHead className="min-w-[180px]">Email</TableHead>
                <TableHead className="min-w-[150px]">Address Line 1</TableHead>
                <TableHead className="min-w-[100px]">City</TableHead>
                <TableHead className="min-w-[100px]">Postcode</TableHead>
                <TableHead className="min-w-[120px]">Phone</TableHead>
                <TableHead className="min-w-[120px]">Bank Name</TableHead>
                <TableHead className="min-w-[150px]">Bank Account Holder</TableHead>
                <TableHead className="min-w-[140px]">Bank Account Number</TableHead>
                <TableHead className="min-w-[120px]">Bank Sort Code</TableHead>
                <TableHead className="min-w-[150px]">Employment Start Date</TableHead>
                <TableHead className="min-w-[100px]">Pay Basis</TableHead>
                <TableHead className="min-w-[100px]">Pay Group</TableHead>
                <TableHead className="min-w-[150px]">Function Areas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={18} className="h-24 text-center">
                    <Spinner className="mx-auto" />
                  </TableCell>
                </TableRow>
              ) : paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={18} className="h-24 text-center text-palette-jungleGreen500">
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.person_id} />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <DataCell value={record.first_name} />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <DataCell value={record.surname} />
                    </TableCell>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.status} />
                    </TableCell>
                    <TableCell className="min-w-[110px]">
                      <DataCell
                        value={record.date_of_birth}
                        formatValue={(val) => (val ? format(new Date(val), "dd/MM/yyyy") : "-")}
                      />
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <DataCell value={record.email} />
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      <DataCell value={record.address_line_1} />
                    </TableCell>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.city} />
                    </TableCell>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.postcode} />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <DataCell value={record.phone_number} />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <DataCell value={record.bank_name} />
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      <DataCell value={record.name_on_bank_account} />
                    </TableCell>
                    <TableCell className="min-w-[140px]">
                      <DataCell value={record.bank_account_number} />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <DataCell value={record.bank_sort_code} />
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      <DataCell
                        value={record.contract_start_date}
                        formatValue={(val) => (val ? format(new Date(val), "dd/MM/yyyy") : "-")}
                      />
                    </TableCell>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.pay_basis} />
                    </TableCell>
                    <TableCell className="min-w-[100px]">
                      <DataCell value={record.pay_group} />
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      <DataCell value={record.sheriff_areas} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading records:{" "}
            {error instanceof Error ? error.message : "An unknown error occurred"}
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

export default ComplianceDataCompletenessTab
